// src/pages/HostDashboard.tsx - Beautiful Host Dashboard with Logout
import React from 'react';
import {useQuery, useMutation} from '@tanstack/react-query';
import {supabase} from '@/services/supabase';
import {useAuth} from '@/context/AuthContext';
import {
    Play,
    Clock,
    AlertTriangle,
    LogOut,
    User,
    GamepadIcon,
    Calendar,
    Shield,
    RefreshCw,
    ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';

interface GameAccess {
    game_name: string;
    game_display_name: string;
    game_description: string | null;
    game_url: string;
    game_thumbnail: string | null;
    expires_at: string | null;
    granted_at: string;
}

const HostDashboard: React.FC = () => {
    const {user, signOut} = useAuth();

    // Fetch host's available games
    const {
        data: availableGames = [],
        isLoading: gamesLoading,
        error: gamesError,
        refetch: refetchGames
    } = useQuery<GameAccess[]>({
        queryKey: ['host-available-games', user?.email],
        queryFn: async (): Promise<GameAccess[]> => {
            if (!user?.email) return [];

            const {data, error} = await supabase
                .rpc('get_host_available_games', {
                    p_host_email: user.email
                });

            if (error) {
                console.error('Error fetching available games:', error);
                throw error;
            }

            return data || [];
        },
        enabled: !!user?.email && user.role === 'host',
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Game access mutation
    const accessGame = useMutation({
        mutationFn: async (gameName: string) => {
            if (!user?.email) throw new Error('User not authenticated');

            const {data, error} = await supabase.functions.invoke('manage-game-access', {
                body: {
                    action: 'grant-access',
                    gameName: gameName
                }
            });

            if (error) {
                // supabase-js wraps a non-2xx response as FunctionsHttpError with the
                // raw Response in `.context`. The edge function returns { error: "<reason>" },
                // so surface that real reason (e.g. "Access denied…") instead of the generic
                // "Edge function returned a non-2xx status code" that confuses users.
                let message = error.message || 'Failed to access game';
                const context = (error as { context?: Response }).context;
                if (context && typeof context.json === 'function') {
                    try {
                        const body = await context.json();
                        if (body?.error) message = body.error;
                    } catch {
                        // keep the generic message if the body can't be parsed
                    }
                }
                throw new Error(message);
            }

            if (data?.error) {
                throw new Error(data.error);
            }

            if (!data?.data?.loginUrl) {
                throw new Error('Failed to generate game access link');
            }

            return data.data;
        },
        onSuccess: (data) => {
            window.open(data.loginUrl, '_blank', 'noopener,noreferrer');
        },
        onError: (error) => {
            toast.error(`Failed to access game: ${error.message}`);
        }
    });

    const handleLogout = async () => {
        try {
            await signOut();
            toast.success('Logged out successfully');
        } catch (error) {
            console.error('Logout error:', error);
            toast.error('Failed to logout');
        }
    };

    const formatExpirationDate = (expiresAt: string | null) => {
        if (!expiresAt) return null;

        const date = new Date(expiresAt);
        const now = new Date();
        const diffHours = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60));

        if (diffHours < 0) return {text: 'Expired', isExpired: true};
        if (diffHours < 1) return {text: 'Expires soon', isExpired: false};
        if (diffHours < 24) return {text: `Expires in ${diffHours}h`, isExpired: false};

        const diffDays = Math.ceil(diffHours / 24);
        return {text: `Expires in ${diffDays}d`, isExpired: false};
    };

    const formatGrantedDate = (grantedAt: string) => {
        const date = new Date(grantedAt);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
        });
    };

    if (gamesLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div
                        className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo/Title */}
                        <div className="flex items-center space-x-3">
                            <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <GamepadIcon className="h-5 w-5 text-white"/>
                            </div>
                            <div>
                                <p className="mt-1">
                                    Ron2Game Marketplace
                                </p>
                            </div>
                        </div>

                        {/* User Info & Actions */}
                        <div className="flex items-center space-x-4">
                            {/* Refresh Button */}
                            <button
                                onClick={() => refetchGames()}
                                disabled={gamesLoading}
                                className="p-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-100 transition-colors"
                                title="Refresh Games"
                            >
                                <RefreshCw className={`h-5 w-5 ${gamesLoading ? 'animate-spin' : ''}`}/>
                            </button>

                            {/* User Profile */}
                            <div className="flex items-center space-x-3 text-sm">
                                <div className="hidden sm:block text-right">
                                    <p className="font-medium text-gray-900">{user?.full_name || 'Host User'}</p>
                                    <p className="text-gray-500">{user?.email}</p>
                                </div>
                                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <User className="h-5 w-5 text-blue-600"/>
                                </div>
                            </div>

                            {/* Logout Button */}
                            <button
                                onClick={handleLogout}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                            >
                                <LogOut className="h-4 w-4 mr-2"/>
                                <span className="hidden sm:inline">Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Welcome Section */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Welcome back, {user?.first_name || 'Host'}!
                    </h2>
                    <p className="text-gray-600">
                        Access your assigned games and create interactive sessions for your audience.
                    </p>
                </div>

                {/* Error State */}
                {gamesError && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                            <AlertTriangle className="h-5 w-5 text-red-500"/>
                            <p className="text-red-700">
                                Failed to load games: {gamesError.message}
                            </p>
                            <button
                                onClick={() => refetchGames()}
                                className="ml-auto text-red-600 hover:text-red-800 underline text-sm"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                )}

                {/* Games Grid */}
                {availableGames.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {availableGames.map((game) => {
                            const expiration = formatExpirationDate(game.expires_at);
                            const isAccessingThis = accessGame.isPending && accessGame.variables === game.game_name;

                            return (
                                <div
                                    key={game.game_name}
                                    className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200 overflow-hidden"
                                >
                                    {/* Game Thumbnail */}
                                    <div
                                        className="relative h-48 overflow-hidden bg-slate-900 flex items-center justify-center">
                                        {game.game_thumbnail ? (
                                            <>
                                                {/* Blurred copy of the logo fills the card edge-to-edge so
                                                    the backdrop always matches the logo's own colors (no
                                                    floating-box seam), regardless of the logo's aspect ratio. */}
                                                <img
                                                    src={game.game_thumbnail}
                                                    aria-hidden="true"
                                                    className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl"
                                                />
                                                <img
                                                    src={game.game_thumbnail}
                                                    alt={game.game_display_name}
                                                    className="relative max-h-full max-w-full object-contain p-4"
                                                />
                                            </>
                                        ) : (
                                            <GamepadIcon className="h-16 w-16 text-white opacity-80"/>
                                        )}
                                    </div>

                                    {/* Game Content */}
                                    <div className="p-6">
                                        <div className="mb-4">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                                {game.game_display_name}
                                            </h3>
                                            {game.game_description && (
                                                <p className="text-sm text-gray-600 line-clamp-2">
                                                    {game.game_description}
                                                </p>
                                            )}
                                        </div>

                                        {/* Game Info */}
                                        <div className="space-y-2 mb-4 text-sm text-gray-500">
                                            <div className="flex items-center space-x-2">
                                                <Calendar className="h-4 w-4"/>
                                                <span>Added {formatGrantedDate(game.granted_at)}</span>
                                            </div>
                                            {expiration && (
                                                <div className="flex items-center space-x-2">
                                                    <Clock className="h-4 w-4"/>
                                                    <span className={expiration.isExpired ? 'text-red-600' : ''}>
                                                        {expiration.text}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Access Button */}
                                        <button
                                            onClick={() => accessGame.mutate(game.game_name)}
                                            disabled={isAccessingThis || (expiration?.isExpired)}
                                            className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium transition-colors ${
                                                expiration?.isExpired
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : isAccessingThis
                                                        ? 'bg-blue-100 text-blue-600'
                                                        : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                            }`}
                                        >
                                            {isAccessingThis ? (
                                                <>
                                                    <div
                                                        className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                                                    Connecting...
                                                </>
                                            ) : expiration?.isExpired ? (
                                                <>
                                                    <Shield className="h-4 w-4 mr-2"/>
                                                    Access Expired
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="h-4 w-4 mr-2"/>
                                                    Access Game
                                                    <ExternalLink className="h-4 w-4 ml-2"/>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* Empty State */
                    <div className="text-center py-12">
                        <div
                            className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                            <GamepadIcon className="h-12 w-12 text-gray-400"/>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No games available
                        </h3>
                        <p className="text-gray-500 mb-6">
                            You don't have access to any games yet. Contact your administrator to get started.
                        </p>
                        <button
                            onClick={() => refetchGames()}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            <RefreshCw className="h-4 w-4 mr-2"/>
                            Check Again
                        </button>
                    </div>
                )}

                {/* Footer Info */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                    <div className="text-center text-sm text-gray-500">
                        <p>
                            Having trouble accessing your games? Contact your administrator for support.
                        </p>
                        <p className="mt-1">
                            Ron2Game Marketplace
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default HostDashboard;
